import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { companySignin, authentication, googleSigninAction } from '../actions/auth';
import logo from '../assets/images/logo.png';
import './CompanySignin.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_SIGNUP_REQUIRED_ERROR = 'No approved company dashboard account found with this Google email.';

const decodeGoogleCredential = (credential) => {
    try {
        const [, payloadPart] = String(credential || '').split('.');
        if (!payloadPart) {
            return null;
        }
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
        const json = atob(padded);
        return JSON.parse(json);
    } catch (error) {
        return null;
    }
};

const CompanySignin = () => {
    const history = useHistory();
    const publicNavLinks = [
        { label: 'HOME', href: 'https://infoethiopia.net' },
        { label: 'CATEGORIES', href: 'https://infoethiopia.net/categories' },
        { label: 'JOBS', href: 'https://infoethiopia.net/jobs' },
        { label: 'CONTACT US', href: 'https://infoethiopia.net/contact-us' },
    ];
    const [values, setValues] = useState({
        email: '',
        password: '',
        loading: false,
        googleLoading: false,
        error: '',
        showPassword: false,
    });

    const { email, password, loading, googleLoading, error, showPassword } = values;

    const handleGoogleResponse = useCallback((response) => {
        if (!response || !response.credential) {
            setValues(v => ({ ...v, error: 'Google sign-in failed. Please try again.', googleLoading: false }));
            return;
        }
        const googleProfile = decodeGoogleCredential(response.credential);
        setValues(v => ({ ...v, googleLoading: true, error: '' }));
        googleSigninAction(response.credential).then((data) => {
            if (data.err) {
                if (data.err === GOOGLE_SIGNUP_REQUIRED_ERROR) {
                    history.push({
                        pathname: '/company/signup',
                        state: {
                            googlePrefill: {
                                email: googleProfile?.email || '',
                            },
                        },
                    });
                    return;
                }
                setValues(v => ({ ...v, error: data.err, googleLoading: false }));
            } else {
                authentication(data, () => { history.push('/admin/company-dashboard'); });
            }
        });
    }, [history]);

    // Load Google Identity Services and initialize once loaded
    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return;

        const init = () => {
            if (window.google && window.google.accounts) {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: handleGoogleResponse,
                    ux_mode: 'popup',
                });
            }
        };

        if (window.google && window.google.accounts) {
            init();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = init;
        document.head.appendChild(script);

        return () => {
            if (window.google && window.google.accounts) {
                window.google.accounts.id.cancel();
            }
        };
    }, [handleGoogleResponse]);

    const handleChange = (name) => (e) => {
        setValues({ ...values, error: '', [name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setValues({ ...values, loading: true });

        companySignin({ email: email.trim(), password }).then((data) => {
            if (data.err) {
                setValues({ ...values, error: data.err, loading: false });
            } else {
                authentication(data, () => {
                    history.push('/admin/company-dashboard');
                });
            }
        });
    };

    const handleGoogleClick = () => {
        if (!GOOGLE_CLIENT_ID) {
            setValues(v => ({ ...v, error: 'Google sign-in is not configured. Please contact support.' }));
            return;
        }
        if (window.google && window.google.accounts) {
            const hiddenBtn = document.getElementById('g-hidden-btn');
            if (hiddenBtn) {
                window.google.accounts.id.renderButton(hiddenBtn, {
                    theme: 'outline',
                    size: 'large',
                    width: 400,
                    type: 'standard',
                });
                const btn = hiddenBtn.querySelector('div[role=button]');
                if (btn) {
                    btn.click();
                } else {
                    setValues(v => ({
                        ...v,
                        error: 'Google sign-in could not be opened. Please try again.',
                        googleLoading: false,
                    }));
                }
            }
        } else {
            setValues(v => ({ ...v, error: 'Google is still loading. Please wait a moment and try again.' }));
        }
    };

    return (
        <div className="company-signin">
            {/* Animated Background */}
            <div className="signin-background">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
            </div>

            <nav className="signin-public-nav" aria-label="Public navigation">
                <div className="signin-public-nav-inner">
                    {publicNavLinks.map((item) => (
                        <a
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="signin-public-nav-link"
                        >
                            {item.label}
                        </a>
                    ))}
                </div>
            </nav>

            {/* Main Content */}
            <div className="signin-container">
                {/* Left Side - Branding */}
                <div className="signin-branding">
                    <div className="brand-content">
                        <h1 className="brand-title">
                            Welcome to <span className="highlight">InfoEthiopia</span>
                        </h1>
                        <p className="brand-subtitle">
                            Your gateway to Ethiopia's premier business directory
                        </p>
                        <div className="brand-features">
                            <div className="feature-item">
                                <div className="feature-icon">📰</div>
                                <span>Manage Your News</span>
                            </div>
                            <div className="feature-item">
                                <div className="feature-icon">💼</div>
                                <span>Post Job Opportunities</span>
                            </div>
                            <div className="feature-item">
                                <div className="feature-icon">👤</div>
                                <span>Manage Your Profile</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side - Sign In Form */}
                <div className="signin-form-section">
                    <div className="form-card">
                        <div className="form-logo">
                            <img src={logo} alt="Info Ethiopia" className="form-logo-image" />
                        </div>
                        <div className="form-header">
                            <h2>Sign In</h2>
                            <p>Enter your credentials to access your account</p>
                        </div>

                        {error && (
                            <div className="error-message">
                                <span className="error-icon">⚠️</span>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="signin-form">
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <div className="input-wrapper">
                                    <span className="input-icon">✉️</span>
                                    <input
                                        type="email"
                                        id="email"
                                        value={email}
                                        onChange={handleChange('email')}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="input-wrapper">
                                    <span className="input-icon">🔒</span>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        value={password}
                                        onChange={handleChange('password')}
                                        placeholder="Enter your password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() =>
                                            setValues({ ...values, showPassword: !showPassword })
                                        }
                                    >
                                        {showPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>

                            <div className="form-options">
                                <label className="remember-me">
                                    <input type="checkbox" />
                                    <span>Remember me</span>
                                </label>
                                <a href="/company/forgot-password" className="forgot-password">
                                    Forgot Password?
                                </a>
                            </div>

                            <button type="submit" className="signin-button" disabled={loading || googleLoading}>
                                {loading ? (
                                    <>
                                        <span className="spinner"></span>
                                        Signing In...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </button>

                            {/* Divider */}
                            <div className="divider">
                                <span className="divider-line"></span>
                                <span className="divider-text">or</span>
                                <span className="divider-line"></span>
                            </div>

                            {/* Google Sign In Button */}
                            <button
                                type="button"
                                className="google-signin-button"
                                onClick={handleGoogleClick}
                                disabled={loading || googleLoading}
                                id="google-signin-btn"
                            >
                                {googleLoading ? (
                                    <>
                                        <span className="spinner spinner-dark"></span>
                                        Signing in with Google...
                                    </>
                                ) : (
                                    <>
                                        <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                        </svg>
                                        Continue with Google
                                    </>
                                )}
                            </button>

                            {/* Hidden div for Google button popup fallback */}
                            <div id="g-hidden-btn" style={{ display: 'none' }}></div>

                            <div className="signup-prompt">
                                <span>Don't have an account?</span>
                                <button
                                    type="button"
                                    className="signup-link-button"
                                    onClick={() => history.push('/company/signup')}
                                >
                                    Sign Up
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanySignin;
